import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Formik } from 'formik';
import {
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
} from "@/layout/AppComponents";
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { axiosPath } from '../../../../routing/helpers/router';
import Translator from '../../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../store/actions/currentPageAction';
import ModuleComsaEnvironmentalSideBarReports from '../ModuleComsaEnvironmentalSideBarReports';
import industrialDeconstructionNormalAspects from './constants/industrialDeconstructionNormalAspects';
import AppSubmitButton from '../../../../layout/components/form/AppSubmitButton';
import ModuleComsaEnvironmentalIndustrialDeconstructionNormalAspectsCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalIndustrial/ModuleComsaEnvironmentalIndustrialDeconstructionNormalAspectsCard';
import ModuleComsaEnvironmentalTitleCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalTitleCard';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';

export default function ModuleComsaEnvironmentalIndustrialDeconstructionNormalAspectsScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [initialValues, setInitialValues] = useState();
  const [filterApplicable, setFilterApplicable] = useState(false);

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "industrialDeconstructionNormalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(r.data.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > SAVE FORM //
  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "industrialDeconstructionNormalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(r.data?.sheetData);
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );
  // < SAVE FORM //

  const renderCard = useCallback(
    (values, setFieldValue) =>
      industrialDeconstructionNormalAspects.map((iuna, i) => (
        <React.Fragment key={i}>
          <ModuleComsaEnvironmentalIndustrialDeconstructionNormalAspectsCard
            index={i}
            filterApplicable={filterApplicable}
            values={values}
            setFieldValue={(n, v) => setFieldValue(n, v)}
            motive={iuna.motive}
            subphase={iuna.subphase}
          />
        </React.Fragment>
      )),
    [initialValues, industrialDeconstructionNormalAspects, filterApplicable]
  );

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="industrial"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_industrial_deconstruction_normal_aspects" />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator
                file="module-comsaEnvironmental"
                name="deconstruction.normals"
              />
            </Title>
          </div>
        </div>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
            enableReinitialize
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <FilterApplicable
                  className="mt-2 mb-2"
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="filter.applicable"
                    />
                  )}
                  onClick={() => setFilterApplicable(!filterApplicable)}
                  $filtered={filterApplicable}
                >
                  <AppRemixIcon icon="filter" className="mr-2" size={16} />
                  {filterApplicable ? (
                    <Translator
                      file="module-comsaEnvironmental"
                      name="remove.applicable.filter"
                    />
                  ) : (
                    <Translator
                      file="module-comsaEnvironmental"
                      name="filter.applicable"
                    />
                  )}
                </FilterApplicable>
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="atmospheric.emissions"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="air.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(0, 4)}
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.vibrations"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="noise.pollution"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(4, 6)}
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="discharges"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="water.soil.contamination"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(6, 9)}
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="resources"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="depletion.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(9, 14)}
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.land.occupation.water.contamination.soil.contamination"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(14, 32)}
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="landscape.alteration"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(32, 33)}
                <ModuleComsaEnvironmentalTitleCard color="primary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="impact.mobility"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="socioeconomic"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(33, 34)}
                <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="order.cleaning"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="environmental.impact"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="visual.impact.soil.alteration"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                {renderCard(values, setFieldValue).slice(34, 35)}
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;

const FilterApplicable = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background-color: #28a745;
  color: #fff;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid #28a745;
  color: ${(props) => (props.$filtered ? '#28a745' : '#fff')};
  background-color: ${(props) => (props.$filtered ? '#fff' : '#28a745')};
`;
